import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';
import FreeMarketplaceRedirect from './components/FreeMarketplaceRedirect';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Register from './pages/Register';
import PublicSearch from './pages/PublicSearch';
import TradesPersonDashboard from './pages/TradesPersonDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import TradespersonOnboarding from './pages/onboarding/TradespersonOnboarding';
import EmployerOnboarding from './pages/onboarding/EmployerOnboarding';
import EmployerPendingApproval from './pages/onboarding/EmployerPendingApproval';
import AdminDashboard from './pages/AdminDashboard';
import AuthCallback from './pages/AuthCallback';
import TradesPersonProfile from './pages/TradesPersonProfile';
import Messaging from './pages/Messaging';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminCredentialVerification from './pages/AdminCredentialVerification';
import AdminSettings from './pages/AdminSettings';
import AdminFAQManagement from './pages/AdminFAQManagement';
import AdminReviewManagement from './pages/AdminReviewManagement';
import AdminReports from './pages/AdminReports';
import AdminCMS from './pages/AdminCMS';
import AdminFeedback from './pages/AdminFeedback';
import AdminSocialLinks from './pages/AdminSocialLinks';
import AdminRegions from './pages/AdminRegions';
import AdminWaitlist from './pages/AdminWaitlist';
import AdminWaitlistDetail from './pages/AdminWaitlistDetail';
import AdminPWASettings from './pages/AdminPWASettings';
import AdminOfferTemplates from './pages/AdminOfferTemplates';
import AdminSupportInbox from './pages/AdminSupportInbox';
import AdminChatCreditLogs from './pages/AdminChatCreditLogs';
import AdminManagement from './pages/AdminManagement';
import AdminUserControl from './pages/AdminUserControl';
import AdminUserProfile from './pages/AdminUserProfile';
import AdminReportsQueue from './pages/AdminReportsQueue';
import AdminVerificationQueue from './pages/AdminVerificationQueue';
import AdminModerationQueue from './pages/AdminModerationQueue';
import AdminPendingApprovals from './pages/AdminPendingApprovals';
import AdminEmployerQueue from './pages/AdminEmployerQueue';
import AdminFunnel from './pages/AdminFunnel';
import AdminReferralMonitoring from './pages/AdminReferralMonitoring';
import AdminGrandfatherRules from './pages/AdminGrandfatherRules';
import AdminGrandfatherMembers from './pages/AdminGrandfatherMembers';
import AdminWeeklyReports from './pages/AdminWeeklyReports';
import AdminEmailSettings from './pages/AdminEmailSettings';
import AdminOnCallSettings from './pages/AdminOnCallSettings';
import AdminSiteContent from './pages/AdminSiteContent';
import AdminHiringSettings from './pages/AdminHiringSettings';
import AdminBannerManager from './pages/AdminBannerManager';
import AdminEmailBroadcast from './pages/AdminEmailBroadcast';
import AdminLaunchMigration from './pages/AdminLaunchMigration';
import ReportBug from './pages/ReportBug';
import WaitlistStatus from './pages/WaitlistStatus';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';

import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import HelpFeedback from './pages/HelpFeedback';
import UserSettings from './pages/UserSettings';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ImpersonationBanner from './components/ImpersonationBanner';
import DemoTourOverlay from './components/DemoTourOverlay';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import { CookieBanner } from './components/CookiePreferences';
import { SiteContentProvider } from './hooks/useSiteContent';
import { Toaster } from './components/ui/sonner';
import { registerServiceWorker } from './services/pushNotifications';
import PromoBanner from './components/PromoBanner';
import './App.css';

function AppRouter() {
  const location = useLocation();
  
  // Register service worker on app load
  useEffect(() => {
    registerServiceWorker();
  }, []);
  
  // Scroll to top on route change to prevent blank-looking pages
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <PromoBanner />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/search" element={<PublicSearch />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/tradesperson/dashboard" element={<TradesPersonDashboard />} />
      <Route path="/employer/dashboard" element={<EmployerDashboard />} />
      {/* Phase 3 onboarding — full-screen mobile-friendly flows */}
      <Route path="/onboarding/tradesperson" element={<TradespersonOnboarding />} />
      <Route path="/onboarding/employer" element={<EmployerOnboarding />} />
      <Route path="/onboarding/employer/pending" element={<EmployerPendingApproval />} />
      <Route path="/employer/subscription-success" element={<SubscriptionSuccess />} />
      {/* Free-marketplace: historical paid URLs → informational redirect. */}
      <Route path="/plans" element={<FreeMarketplaceRedirect />} />
      <Route path="/pricing" element={<FreeMarketplaceRedirect />} />
      <Route path="/subscription" element={<FreeMarketplaceRedirect />} />
      <Route path="/subscription-plans" element={<FreeMarketplaceRedirect />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/users" element={<AdminUserManagement />} />
      <Route path="/admin/credentials" element={<AdminCredentialVerification />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/launch-migration" element={<AdminLaunchMigration />} />
      <Route path="/admin/faqs" element={<AdminFAQManagement />} />
      <Route path="/admin/reviews" element={<AdminReviewManagement />} />
      <Route path="/admin/reports" element={<AdminReports />} />
      <Route path="/admin/cms" element={<AdminCMS />} />
      <Route path="/admin/feedback" element={<AdminFeedback />} />
      <Route path="/admin/social-links" element={<AdminSocialLinks />} />
      <Route path="/admin/regions" element={<AdminRegions />} />
      <Route path="/admin/waitlist" element={<AdminWaitlist />} />
      <Route path="/admin/waitlist/:entryId" element={<AdminWaitlistDetail />} />
      <Route path="/admin/pwa-settings" element={<AdminPWASettings />} />
      <Route path="/admin/offer-templates" element={<AdminOfferTemplates />} />
      <Route path="/admin/support-inbox" element={<AdminSupportInbox />} />
      <Route path="/admin/chat-credit-logs" element={<AdminChatCreditLogs />} />
      <Route path="/admin/management" element={<AdminManagement />} />
      <Route path="/admin/user-control" element={<AdminUserControl />} />
      <Route path="/admin/user/:userId" element={<AdminUserProfile />} />
      <Route path="/admin/reports-queue" element={<AdminReportsQueue />} />
      <Route path="/admin/verification-queue" element={<AdminVerificationQueue />} />
      <Route path="/admin/moderation" element={<AdminModerationQueue />} />
      <Route path="/admin/pending-approvals" element={<AdminPendingApprovals />} />
      <Route path="/admin/employers" element={<AdminEmployerQueue />} />
      <Route path="/admin/funnel" element={<AdminFunnel />} />
      <Route path="/admin/referrals" element={<AdminReferralMonitoring />} />
      <Route path="/admin/grandfather-rules" element={<AdminGrandfatherRules />} />
      <Route path="/admin/grandfather-members" element={<AdminGrandfatherMembers />} />
      <Route path="/admin/weekly-reports" element={<AdminWeeklyReports />} />
      <Route path="/admin/email-settings" element={<AdminEmailSettings />} />
      <Route path="/admin/on-call-settings" element={<AdminOnCallSettings />} />
      <Route path="/admin/site-content" element={<AdminSiteContent />} />
      <Route path="/admin/hiring-settings" element={<AdminHiringSettings />} />
      <Route path="/admin/dashboard-banners" element={<AdminBannerManager />} />
      <Route path="/admin/email-broadcast" element={<AdminEmailBroadcast />} />
      <Route path="/report-bug" element={<ReportBug />} />
      <Route path="/waitlist-status" element={<WaitlistStatus />} />
      <Route path="/profile/:userId" element={<TradesPersonProfile />} />
      <Route path="/messages" element={<Messaging />} />
      <Route path="/help" element={<HelpFeedback />} />
      <Route path="/settings" element={<UserSettings />} />
      {/* Legal Pages */}
      <Route path="/cookies" element={<CookiePolicy />} />
      {/* Retired paid-model pages — redirect to current Terms. */}
      <Route path="/refund-policy" element={<Navigate to="/terms" replace />} />
      <Route path="/cancellation-policy" element={<Navigate to="/terms" replace />} />
      <Route path="/contact" element={<Contact />} />
      {/* Catch-all 404 — must stay last. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <SiteContentProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <AppRouter />
          <DemoTourOverlay />
          <WhatsAppFloatingButton />
          <PWAInstallPrompt />
          <CookieBanner />
          <Toaster position="top-right" />
        </BrowserRouter>
      </SiteContentProvider>
    </div>
  );
}

export default App;
